import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';

/* Standalone-safe apiFetch (matches sibling admin pages) */
async function apiFetch(url, options = {}) {
  const token = localStorage.getItem('bizsuite_token');
  const headers = { ...(options.headers || {}) };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (!options.headers?.['Content-Type'] && !(options.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }
  const res = await fetch(url, { ...options, headers, credentials: 'include' });
  if (res.status === 401) {
    localStorage.removeItem('bizsuite_token');
    window.location.href = '/login.html';
    throw new Error('Unauthorized');
  }
  return res;
}

const TYPE_OPTIONS = ['all', 'text', 'integer', 'decimal', 'date', 'boolean', 'uuid', 'json'];

const TYPE_COLOR = {
  text:    { fg: '#93C5FD', bg: 'rgba(59,130,246,0.18)', bd: 'rgba(59,130,246,0.45)' },
  integer: { fg: '#86EFAC', bg: 'rgba(34,197,94,0.18)',  bd: 'rgba(34,197,94,0.45)'  },
  decimal: { fg: '#6EE7B7', bg: 'rgba(16,185,129,0.18)', bd: 'rgba(16,185,129,0.45)' },
  date:    { fg: '#FCD34D', bg: 'rgba(245,158,11,0.18)', bd: 'rgba(245,158,11,0.45)' },
  boolean: { fg: '#F9A8D4', bg: 'rgba(236,72,153,0.18)', bd: 'rgba(236,72,153,0.45)' },
  uuid:    { fg: '#C4B5FD', bg: 'rgba(139,92,246,0.18)', bd: 'rgba(139,92,246,0.45)' },
  json:    { fg: '#FCA5A5', bg: 'rgba(239,68,68,0.18)',  bd: 'rgba(239,68,68,0.45)'  },
};

const S = {
  container: { padding: '24px 0' },

  topBar: {
    display: 'grid', gridTemplateColumns: '2fr 3fr auto', gap: 12,
    marginBottom: 16, alignItems: 'flex-end',
  },
  field: { display: 'flex', flexDirection: 'column', gap: 4 },
  label: { fontSize: 11, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.5 },
  input: {
    padding: '8px 10px', fontSize: 13, background: 'rgba(15,23,42,0.6)',
    border: '1px solid rgba(255,255,255,0.08)', borderRadius: 6, color: '#E2E8F0',
    boxSizing: 'border-box', width: '100%',
  },
  select: {
    padding: '8px 10px', fontSize: 13, background: 'rgba(15,23,42,0.6)',
    border: '1px solid rgba(255,255,255,0.08)', borderRadius: 6, color: '#E2E8F0',
    width: '100%',
  },
  saveBtn: {
    padding: '9px 18px', background: 'rgba(59,130,246,0.7)',
    border: '1px solid rgba(59,130,246,0.9)', color: '#fff',
    borderRadius: 6, fontWeight: 700, fontSize: 13, cursor: 'pointer',
    whiteSpace: 'nowrap',
  },

  filterBar: {
    display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12,
  },
  filterGroup: {
    background: 'rgba(15,23,42,0.5)',
    border: '1px solid rgba(255,255,255,0.06)', borderRadius: 8,
    padding: 10,
  },
  filterTitle: {
    fontSize: 11, fontWeight: 700, color: '#94A3B8',
    textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6,
  },
  filterRow: { display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 8 },

  canvas: {
    position: 'relative', minHeight: 480,
  },
  panels: {
    display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 80,
    minHeight: 480,
  },
  rail: {
    background: 'rgba(10,14,24,0.92)',
    border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10,
    padding: 12, minHeight: 480, maxHeight: 560,
    overflowY: 'auto',
    display: 'flex', flexDirection: 'column',
  },
  svgOverlay: {
    position: 'absolute', inset: 0, pointerEvents: 'none',
    width: '100%', height: '100%', zIndex: 5,
  },
  railTitle: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    fontSize: 12, fontWeight: 700, color: '#CBD5E1',
    paddingBottom: 8, borderBottom: '1px solid rgba(255,255,255,0.05)',
    marginBottom: 8,
  },

  field_node: {
    display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px',
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 8, marginBottom: 6, fontSize: 12.5,
    transition: 'all 0.15s ease',
  },
  fieldName: { fontWeight: 600, color: '#E2E8F0', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  typeChip: {
    fontSize: 10, fontWeight: 700, padding: '2px 7px',
    borderRadius: 999, textTransform: 'uppercase', letterSpacing: 0.4,
  },
  linkChip: {
    display: 'inline-flex', alignItems: 'center', gap: 3,
    fontSize: 10, fontWeight: 700, padding: '2px 5px',
    borderRadius: 4, color: '#FCD34D', background: 'rgba(245,158,11,0.15)',
    border: '1px solid rgba(245,158,11,0.4)', marginLeft: 4,
  },
  unlinkX: {
    background: 'transparent', border: 'none', color: '#FCA5A5',
    cursor: 'pointer', fontSize: 12, lineHeight: 1, padding: 0,
  },

  emptyHint: {
    color: '#64748B', fontSize: 12, padding: 24, textAlign: 'center',
  },
  errorBanner: {
    background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.4)',
    color: '#FCA5A5', padding: '10px 14px', borderRadius: 6, marginBottom: 12, fontSize: 13,
  },
  successBanner: {
    background: 'rgba(16,185,129,0.15)', border: '1px solid rgba(16,185,129,0.4)',
    color: '#6EE7B7', padding: '10px 14px', borderRadius: 6, marginBottom: 12, fontSize: 13,
  },

  // Saved mappings list
  savedCard: {
    background: 'rgba(30,41,59,0.7)',
    border: '1px solid rgba(255,255,255,0.06)', borderRadius: 10,
    marginTop: 18, padding: 14,
  },
  savedRow: {
    display: 'grid',
    gridTemplateColumns: '1.6fr 1fr 1fr 80px 200px',
    padding: '10px 8px', alignItems: 'center', fontSize: 13,
    borderTop: '1px solid rgba(255,255,255,0.04)',
  },
  savedHeader: {
    display: 'grid',
    gridTemplateColumns: '1.6fr 1fr 1fr 80px 200px',
    padding: '8px',
    fontSize: 11, fontWeight: 700, color: '#94A3B8',
    textTransform: 'uppercase', letterSpacing: 0.5,
  },
  iconBtn: {
    background: 'transparent', border: '1px solid rgba(255,255,255,0.1)', color: '#94A3B8',
    padding: '4px 10px', borderRadius: 5, cursor: 'pointer', fontSize: 11, marginLeft: 4,
  },
};

function TypeChip({ type }) {
  const c = TYPE_COLOR[type] || TYPE_COLOR.text;
  return (
    <span style={{ ...S.typeChip, color: c.fg, background: c.bg, border: `1px solid ${c.bd}` }}>
      {type || 'text'}
    </span>
  );
}

function FieldRow({ field, side, links, onDragStart, onDragEnd, onDrop, onDragOver, onUnlinkPair, onUnlinkAll, isMismatch, isHover }) {
  // links is always an array.
  //   - source side: every (source=field) pair this row participates in (can be many)
  //   - dest side: 0 or 1 element (each dest has at most one source)
  const hasLinks = Array.isArray(links) && links.length > 0;
  const dragHandlers = side === 'source'
    ? {
        draggable: true,                                      // ← always draggable now
        onDragStart: (e) => { e.dataTransfer.setData('text/field', field.name); onDragStart(field); },
        onDragEnd,
      }
    : {
        onDragOver: (e) => { e.preventDefault(); onDragOver(field); },
        onDrop: (e) => { e.preventDefault(); onDrop(field); },
      };
  const baseBg = isHover ? 'rgba(0,224,255,0.10)' : hasLinks ? 'rgba(245,158,11,0.07)' : 'rgba(255,255,255,0.04)';
  const baseBorder = isHover ? '1px dashed rgba(0,224,255,0.7)' : hasLinks ? '1px solid rgba(245,158,11,0.35)' : '1px solid rgba(255,255,255,0.08)';
  return (
    <div
      {...dragHandlers}
      data-side={side}
      data-fname={field.name}
      style={{
        ...S.field_node,
        background: baseBg,
        border: baseBorder,
        cursor: side === 'source' ? 'grab' : 'default',
        flexWrap: 'wrap',                                    // chips wrap on long rows
        rowGap: 4,
      }}
    >
      <span style={S.fieldName} title={field.label || field.name}>{field.label || field.name}</span>
      <TypeChip type={field.type} />

      {/* Per-pair link chips — supports many on the source side, max 1 on dest */}
      {hasLinks && links.map((p) => {
        const otherName = side === 'source' ? p.dest_field : p.source_field;
        const arrow = side === 'source' ? '→' : '←';
        const mismatch = p.source_type !== p.dest_type;
        return (
          <span
            key={`${p.source_field}__${p.dest_field}`}
            style={{
              ...S.linkChip,
              color: mismatch ? '#FCA5A5' : '#FCD34D',
              background: mismatch ? 'rgba(239,68,68,0.15)' : 'rgba(245,158,11,0.15)',
              border: `1px solid ${mismatch ? 'rgba(239,68,68,0.4)' : 'rgba(245,158,11,0.4)'}`,
            }}
            title={`Mapped ${p.source_field} ${arrow} ${p.dest_field}${mismatch ? ' (type mismatch)' : ''}`}
          >
            {arrow} {otherName}
            <button
              style={S.unlinkX}
              onClick={(e) => { e.stopPropagation(); onUnlinkPair && onUnlinkPair(p.source_field, p.dest_field); }}
              title="Remove this mapping"
            >×</button>
          </span>
        );
      })}

      {/* Generic mismatch hint when caller passes isMismatch (used pre-link state) */}
      {!hasLinks && isMismatch && (
        <span style={{ ...S.linkChip, color: '#FCA5A5', background: 'rgba(239,68,68,0.15)', borderColor: 'rgba(239,68,68,0.4)' }}
              title="Type mismatch — data may not transfer cleanly">
          type!
        </span>
      )}
    </div>
  );
}

export default function TableMapping() {
  // ── Toolbar state ──
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [editingId, setEditingId] = useState(null); // null = create mode

  // ── Table catalog ──
  const [standardTables, setStandardTables] = useState([]);
  const [customTables, setCustomTables] = useState([]); // [{ table_name, display_name }]
  const [joinedSources, setJoinedSources] = useState([]); // [{ table_name, display_name }]
  const [savedMappings, setSavedMappings] = useState([]);

  // ── Selection ──
  const [sourceTable, setSourceTable] = useState('');
  const [destTable, setDestTable] = useState('');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [destFilter, setDestFilter] = useState('all');

  // ── Schemas ──
  const [sourceSchema, setSourceSchema] = useState(null); // { columns: [...] }
  const [destSchema, setDestSchema] = useState(null);

  // ── Mapping in progress (source_field -> dest_field) ──
  const [fieldMap, setFieldMap] = useState([]); // [{ source_field, source_type, dest_field, dest_type }]

  // ── Drag state ──
  const [dragField, setDragField] = useState(null);
  const [hoverDest, setHoverDest] = useState(null);

  // ── Connector overlay refs ──
  const canvasRef = useRef(null);
  const svgRef = useRef(null);
  const leftRailRef = useRef(null);
  const rightRailRef = useRef(null);
  const [repaintTick, setRepaintTick] = useState(0);
  const repaint = useCallback(() => setRepaintTick((t) => t + 1), []);

  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [saving, setSaving] = useState(false);

  // ── Load tables list ──
  const loadTables = useCallback(async () => {
    try {
      const r = await apiFetch('/api/admin/table-mappings/_tables');
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Failed to load tables');
      setStandardTables(d.standard || []);
      setCustomTables(d.custom || []);
      setJoinedSources(d.joined || []);
    } catch (e) { setError(e.message); }
  }, []);

  const loadMappings = useCallback(async () => {
    try {
      const r = await apiFetch('/api/admin/table-mappings');
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Failed to load mappings');
      setSavedMappings(d.mappings || []);
    } catch (e) { setError(e.message); }
  }, []);

  useEffect(() => { loadTables(); loadMappings(); }, [loadTables, loadMappings]);

  // ── Load source schema when selected ──
  useEffect(() => {
    if (!sourceTable) { setSourceSchema(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await apiFetch(`/api/admin/table-mappings/_schema/${encodeURIComponent(sourceTable)}`);
        const d = await r.json();
        if (!cancelled && r.ok) setSourceSchema(d);
      } catch (_) {}
    })();
    return () => { cancelled = true; };
  }, [sourceTable]);

  // ── Load dest schema when selected ──
  useEffect(() => {
    if (!destTable) { setDestSchema(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await apiFetch(`/api/admin/table-mappings/_schema/${encodeURIComponent(destTable)}`);
        const d = await r.json();
        if (!cancelled && r.ok) setDestSchema(d);
      } catch (_) {}
    })();
    return () => { cancelled = true; };
  }, [destTable]);

  // ── Auto-suggest description from selected tables ──
  useEffect(() => {
    if (!editingId && sourceTable && destTable) {
      const srcLabel = sourceSchema?.display_name || sourceTable;
      const dstLabel = destSchema?.display_name || destTable;
      // Only auto-fill if user hasn't typed something custom
      if (!description || description.startsWith('Mapping from ')) {
        setDescription(`Mapping from ${srcLabel} to ${dstLabel}`);
      }
    }
  }, [sourceSchema, destSchema, sourceTable, destTable, editingId]);

  // ── Filtered field lists ──
  const filteredSource = useMemo(() => {
    const cols = sourceSchema?.columns || [];
    return sourceFilter === 'all' ? cols : cols.filter((c) => c.type === sourceFilter);
  }, [sourceSchema, sourceFilter]);

  const filteredDest = useMemo(() => {
    const cols = destSchema?.columns || [];
    return destFilter === 'all' ? cols : cols.filter((c) => c.type === destFilter);
  }, [destSchema, destFilter]);

  // ── Mapping helpers ──
  // A source field can be linked to MANY destinations (1-to-many).
  // A destination field has at most ONE source (1-to-1 on the dest side).
  const linkedSourceNames = useMemo(() => new Set(fieldMap.map((m) => m.source_field)), [fieldMap]);
  const linkedDestNames = useMemo(() => new Set(fieldMap.map((m) => m.dest_field)), [fieldMap]);
  const linksBySource = useMemo(() => {
    const m = {};
    fieldMap.forEach((p) => {
      if (!m[p.source_field]) m[p.source_field] = [];
      m[p.source_field].push(p);
    });
    return m;
  }, [fieldMap]);
  const linkByDest = useMemo(() => Object.fromEntries(fieldMap.map((m) => [m.dest_field, m])), [fieldMap]);

  const handleDragStart = (field) => setDragField(field);
  const handleDragEnd = () => { setDragField(null); setHoverDest(null); };
  const handleDragOver = (destField) => setHoverDest(destField.name);

  const handleDrop = (destField) => {
    if (!dragField) return;
    // Same source can map to multiple dest fields. But a single (source, dest)
    // pair must not be duplicated — drop is a no-op if that exact pair exists.
    const exists = fieldMap.some((m) => m.source_field === dragField.name && m.dest_field === destField.name);
    if (exists) {
      setError(`"${dragField.name}" is already linked to "${destField.name}"`);
      setDragField(null); setHoverDest(null);
      return;
    }
    const newPair = {
      source_field: dragField.name,
      source_type: dragField.type,
      dest_field: destField.name,
      dest_type: destField.type,
    };
    if (linkedDestNames.has(destField.name)) {
      // Dest already has a source — replace it (each dest has at most 1 source).
      setFieldMap((prev) => prev.filter((m) => m.dest_field !== destField.name).concat([newPair]));
    } else {
      setFieldMap((prev) => prev.concat([newPair]));
    }
    if (dragField.type !== destField.type) {
      setError(`Type mismatch: "${dragField.name}" (${dragField.type}) → "${destField.name}" (${destField.type}). Linked anyway, but data may need conversion.`);
    } else {
      setError(null);
    }
    setDragField(null); setHoverDest(null);
  };

  // Remove a SINGLE pair (source_field + dest_field). Used by both source and
  // dest side `×` buttons via the specific link chip.
  const unlinkPairByNames = (sourceField, destField) => {
    setFieldMap((prev) => prev.filter((m) => !(m.source_field === sourceField && m.dest_field === destField)));
  };

  // Bulk unlink — used by the dest row × button (clears the only link for that dest)
  // and is also exposed to the source row when no specific dest is in scope.
  const unlinkAllForField = (field, side) => {
    if (side === 'dest') {
      setFieldMap((prev) => prev.filter((m) => m.dest_field !== field.name));
    } else {
      setFieldMap((prev) => prev.filter((m) => m.source_field !== field.name));
    }
  };

  // ── Connector redraw plumbing ──
  useEffect(() => {
    window.addEventListener('resize', repaint);
    return () => window.removeEventListener('resize', repaint);
  }, [repaint]);

  useEffect(() => {
    const L = leftRailRef.current, R = rightRailRef.current;
    if (L) L.addEventListener('scroll', repaint);
    if (R) R.addEventListener('scroll', repaint);
    return () => {
      if (L) L.removeEventListener('scroll', repaint);
      if (R) R.removeEventListener('scroll', repaint);
    };
  }, [repaint]);

  useEffect(() => {
    const svg = svgRef.current;
    const canvas = canvasRef.current;
    if (!svg || !canvas) return;
    const cr = canvas.getBoundingClientRect();
    svg.setAttribute('viewBox', `0 0 ${cr.width} ${cr.height}`);
    svg.setAttribute('width', cr.width);
    svg.setAttribute('height', cr.height);
    svg.innerHTML = '';
    const ns = 'http://www.w3.org/2000/svg';

    // Color by source type — match the type chip palette
    const typeColor = (t) => (TYPE_COLOR[t] || TYPE_COLOR.text).fg;

    fieldMap.forEach((m) => {
      const l = canvas.querySelector(`[data-side="source"][data-fname="${CSS.escape(m.source_field)}"]`);
      const r = canvas.querySelector(`[data-side="dest"][data-fname="${CSS.escape(m.dest_field)}"]`);
      if (!l || !r) return;
      const lr = l.getBoundingClientRect();
      const rr = r.getBoundingClientRect();
      const x1 = lr.right - cr.left;
      const y1 = lr.top + lr.height / 2 - cr.top;
      const x2 = rr.left - cr.left;
      const y2 = rr.top + rr.height / 2 - cr.top;
      const midX = (x1 + x2) / 2;

      const mismatch = m.source_type !== m.dest_type;
      const color = mismatch ? '#EF4444' : typeColor(m.source_type);

      const path = document.createElementNS(ns, 'path');
      path.setAttribute('d', `M ${x1} ${y1} C ${midX} ${y1}, ${midX} ${y2}, ${x2} ${y2}`);
      path.setAttribute('stroke', color);
      path.setAttribute('stroke-width', 2);
      path.setAttribute('fill', 'none');
      path.setAttribute('opacity', 0.85);
      if (mismatch) path.setAttribute('stroke-dasharray', '6,4');
      path.setAttribute('filter', `drop-shadow(0 0 6px ${color})`);
      svg.appendChild(path);

      [[x1, y1], [x2, y2]].forEach(([cx, cy]) => {
        const circle = document.createElementNS(ns, 'circle');
        circle.setAttribute('cx', cx);
        circle.setAttribute('cy', cy);
        circle.setAttribute('r', 4);
        circle.setAttribute('fill', color);
        circle.setAttribute('stroke', 'rgba(15,23,42,0.85)');
        circle.setAttribute('stroke-width', 1.5);
        svg.appendChild(circle);
      });
    });
  }, [fieldMap, filteredSource, filteredDest, sourceSchema, destSchema, repaintTick]);

  // ── Persist ──
  const reset = () => {
    setName(''); setDescription(''); setEditingId(null);
    setSourceTable(''); setDestTable('');
    setSourceSchema(null); setDestSchema(null);
    setFieldMap([]); setError(null); setSuccess(null);
  };

  const save = async () => {
    setError(null); setSuccess(null);
    if (!name.trim()) return setError('Mapping Name is required');
    if (!sourceTable || !destTable) return setError('Pick a source and destination table');
    if (!fieldMap.length) return setError('Map at least one field before saving');
    setSaving(true);
    try {
      const body = {
        name: name.trim(),
        description: description || '',
        source_table: sourceTable,
        dest_table: destTable,
        field_map: fieldMap,
      };
      const url = editingId
        ? `/api/admin/table-mappings/${editingId}`
        : '/api/admin/table-mappings';
      const method = editingId ? 'PUT' : 'POST';
      const r = await apiFetch(url, { method, body: JSON.stringify(body) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Save failed');
      setSuccess(editingId ? 'Mapping updated' : 'Mapping saved');
      loadMappings();
      if (!editingId) reset();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const loadIntoEditor = async (m) => {
    setEditingId(m.id);
    setName(m.name);
    setDescription(m.description || '');
    setSourceTable(m.source_table);
    setDestTable(m.dest_table);
    setFieldMap(m.field_map || []);
    setError(null); setSuccess(null);
  };

  const removeMapping = async (m) => {
    if (!confirm(`Delete mapping "${m.name}"?`)) return;
    try {
      const r = await apiFetch(`/api/admin/table-mappings/${m.id}`, { method: 'DELETE' });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Delete failed');
      if (editingId === m.id) reset();
      loadMappings();
    } catch (e) { setError(e.message); }
  };

  const allTablesForSource = useMemo(() => {
    return [
      ...standardTables.map((t) => ({ value: t, label: t, group: 'Standard' })),
      ...customTables.map((t) => ({ value: t.table_name, label: t.display_name + ' (custom)', group: 'Custom' })),
    ];
  }, [standardTables, customTables]);

  return (
    <div style={S.container}>
      {/* ── Top bar ───────────────────────────────────────── */}
      <div style={S.topBar}>
        <div style={S.field}>
          <label style={S.label}>Mapping Name {editingId && <span style={{ color: '#FCD34D', marginLeft: 4 }}>(editing)</span>}</label>
          <input
            type="text"
            style={S.input}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. payroll_to_form24q"
          />
        </div>
        <div style={S.field}>
          <label style={S.label}>Description</label>
          <input
            type="text"
            style={S.input}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Auto-fills with source/dest table names"
          />
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {editingId && (
            <button
              style={{ ...S.saveBtn, background: 'rgba(100,116,139,0.4)', borderColor: 'rgba(100,116,139,0.7)' }}
              onClick={reset}
            >New</button>
          )}
          <button style={S.saveBtn} onClick={save} disabled={saving}>
            {saving ? 'Saving...' : (editingId ? 'Update Mapping' : 'Save Mapping')}
          </button>
        </div>
      </div>

      {error && <div style={S.errorBanner}>{error}</div>}
      {success && <div style={S.successBanner}>{success}</div>}

      {/* ── Filter bar ─────────────────────────────────────── */}
      <div style={S.filterBar}>
        <div style={S.filterGroup}>
          <div style={S.filterTitle}>Source Table</div>
          <div style={S.filterRow}>
            <select style={S.select} value={sourceTable} onChange={(e) => { setSourceTable(e.target.value); setFieldMap([]); }}>
              <option value="">Select source table…</option>
              {joinedSources.length > 0 && (
                <optgroup label="Joined Sources">
                  {joinedSources.map((t) => <option key={t.table_name} value={t.table_name}>🔗 {t.display_name}</option>)}
                </optgroup>
              )}
              <optgroup label="Standard">
                {standardTables.map((t) => <option key={t} value={t}>{t}</option>)}
              </optgroup>
              <optgroup label="Custom">
                {customTables.map((t) => <option key={t.table_name} value={t.table_name}>{t.display_name}</option>)}
              </optgroup>
            </select>
            <select style={S.select} value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)}>
              {TYPE_OPTIONS.map((t) => <option key={t} value={t}>{t === 'all' ? 'All field types' : t}</option>)}
            </select>
          </div>
        </div>
        <div style={S.filterGroup}>
          <div style={S.filterTitle}>Destination Table</div>
          <div style={S.filterRow}>
            <select style={S.select} value={destTable} onChange={(e) => { setDestTable(e.target.value); setFieldMap([]); }}>
              <option value="">Select destination table…</option>
              {customTables.length === 0 && <option disabled>(no custom tables yet)</option>}
              {customTables.map((t) => <option key={t.table_name} value={t.table_name}>{t.display_name}</option>)}
            </select>
            <select style={S.select} value={destFilter} onChange={(e) => setDestFilter(e.target.value)}>
              {TYPE_OPTIONS.map((t) => <option key={t} value={t}>{t === 'all' ? 'All field types' : t}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* ── Two-panel mapping area with SVG connector overlay ── */}
      <div ref={canvasRef} style={S.canvas}>
        <svg ref={svgRef} style={S.svgOverlay} xmlns="http://www.w3.org/2000/svg"></svg>
        <div style={S.panels}>
          <div ref={leftRailRef} style={S.rail}>
            <div style={S.railTitle}>
              <span>Source Fields {sourceSchema && `— ${sourceSchema.display_name || sourceSchema.table_name}`}</span>
              <span style={{ color: '#64748B' }}>{filteredSource.length}</span>
            </div>
          {!sourceTable && <div style={S.emptyHint}>Pick a source table above to see its fields.</div>}
          {sourceTable && filteredSource.length === 0 && (
            <div style={S.emptyHint}>No fields match the type filter.</div>
          )}
            {filteredSource.map((f) => (
              <FieldRow
                key={f.name}
                field={f}
                side="source"
                links={linksBySource[f.name] || []}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                onUnlinkPair={unlinkPairByNames}
                onUnlinkAll={() => unlinkAllForField(f, 'source')}
              />
            ))}
          </div>

          <div ref={rightRailRef} style={S.rail}>
            <div style={S.railTitle}>
              <span>Destination Fields {destSchema && `— ${destSchema.display_name || destSchema.table_name}`}</span>
              <span style={{ color: '#64748B' }}>{filteredDest.length}</span>
            </div>
          {!destTable && <div style={S.emptyHint}>Pick a destination (custom) table above to see its fields.</div>}
          {destTable && filteredDest.length === 0 && (
            <div style={S.emptyHint}>No fields match the type filter.</div>
          )}
            {filteredDest.map((f) => {
              const link = linkByDest[f.name];
              return (
                <FieldRow
                  key={f.name}
                  field={f}
                  side="dest"
                  links={link ? [link] : []}
                  isMismatch={!!(link && link.source_type !== link.dest_type)}
                  isHover={hoverDest === f.name}
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onUnlinkPair={unlinkPairByNames}
                  onUnlinkAll={() => unlinkAllForField(f, 'dest')}
                />
              );
            })}
          </div>
        </div>
      </div>

      {fieldMap.length > 0 && (
        <div style={{ marginTop: 12, fontSize: 12, color: '#94A3B8' }}>
          {fieldMap.length} field{fieldMap.length === 1 ? '' : 's'} mapped
          {fieldMap.some((m) => m.source_type !== m.dest_type) && (
            <span style={{ color: '#FCA5A5', marginLeft: 8 }}>
              · {fieldMap.filter((m) => m.source_type !== m.dest_type).length} type mismatch
              {fieldMap.filter((m) => m.source_type !== m.dest_type).length === 1 ? '' : 'es'}
            </span>
          )}
        </div>
      )}

      {/* ── Saved mappings ─────────────────────────────────── */}
      <div style={S.savedCard}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
          <h3 style={{ margin: 0, fontSize: 14, color: '#E2E8F0' }}>Saved Mappings</h3>
          <span style={{ fontSize: 12, color: '#64748B' }}>{savedMappings.length} mapping{savedMappings.length === 1 ? '' : 's'}</span>
        </div>
        <div style={S.savedHeader}>
          <span>Name</span>
          <span>Source</span>
          <span>Destination</span>
          <span>Fields</span>
          <span style={{ textAlign: 'right' }}>Actions</span>
        </div>
        {savedMappings.length === 0 && (
          <div style={S.emptyHint}>No saved mappings yet.</div>
        )}
        {savedMappings.map((m) => (
          <div key={m.id} style={S.savedRow}>
            <div>
              <div style={{ fontWeight: 700, color: '#E2E8F0' }}>{m.name}</div>
              <div style={{ fontSize: 11, color: '#64748B', marginTop: 2 }}>{m.description}</div>
            </div>
            <code style={{ color: '#94A3B8', fontSize: 12 }}>{m.source_table}</code>
            <code style={{ color: '#94A3B8', fontSize: 12 }}>{m.dest_table}</code>
            <span style={{ color: '#CBD5E1', fontSize: 12 }}>{(m.field_map || []).length}</span>
            <div style={{ textAlign: 'right' }}>
              <button style={S.iconBtn} onClick={() => loadIntoEditor(m)} title="Load mapping into editor">Edit</button>
              <button style={{ ...S.iconBtn, color: '#FCA5A5' }} onClick={() => removeMapping(m)} title="Delete mapping">Delete</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
